// Home screen — migrated to the RitmoIQ design system (handoff_app_design/README.md §5.1).
// Business logic (data shapes, handlers, doctrine) is unchanged from the previous A2 revision —
// only presentation moved onto the shared tokens + components/ui primitives.
// Doctrine kept intact: the forecast shows "סבירות" labels (never a percent — locked decision
// 2026-08-24), the insight card shows the plan-compliance verdict (never history comparisons),
// and feedback posts to the existing context-flag API.
import React, { useState } from 'react';
import {
  Sparkle, ThumbsUp, ThumbsDown, Check, Thermometer, Sun, CaretLeft,
  UserPlus, Gear, ChatCircle, Question, Flag, CalendarBlank, ChartLineUp, Trophy,
} from '@phosphor-icons/react';
import { Card, Section, ListGroup, ListRow, Button, Badge, Metric, PulseLine, Skeleton } from '../ui';

// Workout label + quality/easy classification (label-detection logic preserved verbatim from the
// previous a2WorkoutType; only the color mapping changes — the design system allows exactly one
// accent border color for a "quality" day, not a per-type hue).
function workoutKind(raw) {
  const t = String(raw || '').toLowerCase();
  if (/מנוחה|rest/.test(t)) return { label: 'מנוחה', quality: false };
  if (/אינטרוול|interval|vo2|ריפיטישן|repetition|פארטלק|fartlek/.test(t)) return { label: 'אינטרוולים', quality: true };
  if (/סף|טמפו|tempo|threshold|קרוז|cruise/.test(t)) return { label: 'סף / טמפו', quality: true };
  if (/ארוכ|long/.test(t)) return { label: 'ריצה ארוכה', quality: true };
  if (/מרוץ|race/.test(t)) return { label: 'מרוץ', quality: true };
  return { label: 'ריצה קלה', quality: false };
}

// ── Race countdown (unframed, §5.1) — big number + pulse line + phase badge ──
export function RaceHero({ raceName, raceDistance, days, phaseInfo, onClick }) {
  return (
    <div onClick={onClick} className={onClick ? 'cursor-pointer' : ''} style={{ paddingTop: 6 }}>
      <div className="flex items-end gap-3">
        <span
          className="font-black text-text tabular-nums"
          style={{ fontSize: 88, lineHeight: 0.8, letterSpacing: '-4px' }}
        >{days}</span>
        <div className="pb-1.5">
          <div className="font-bold text-[17px] text-text">ימים למרוץ {raceName}</div>
          {raceDistance && <div className="font-medium text-sm text-text-muted mt-0.5">{raceDistance}</div>}
        </div>
      </div>
      <PulseLine className="mt-3.5" />
      {phaseInfo && (
        <div className="flex items-center gap-2 mt-2.5">
          <Badge tone="positive">{phaseInfo.phase}</Badge>
        </div>
      )}
    </div>
  );
}

// ── Today's / next workout — the primary action, framed (§5.1) ──
export function TodayWorkout({ next, onViewPlan }) {
  if (!next) return null;
  const wk = workoutKind(next.workout_type || next.label);
  // Day cell + today detection (kept verbatim): query_next_planned returns a full ISO `date` but
  // no `day_name`; derive the Hebrew weekday + dd.mm from the date (LOCAL parts — no TZ drift).
  const HEB_DAYS = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
  const iso = String(next.date || '').slice(0, 10);
  let dayCell = next.day_name || null;
  let isToday = false;
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    const [y, m, d] = iso.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    dayCell = `${HEB_DAYS[dt.getDay()]} ${String(d).padStart(2, '0')}.${String(m).padStart(2, '0')}`;
    const now = new Date();
    isToday = dt.getFullYear() === now.getFullYear() && dt.getMonth() === now.getMonth()
      && dt.getDate() === now.getDate();
  } else {
    try {
      isToday = (next.day_name || '').includes(HEB_DAYS[new Date().getDay()]);
    } catch { /* keep false */ }
  }
  return (
    <Card accentSide={wk.quality ? 'quality' : undefined}>
      <div className="flex items-center justify-between">
        <span className="font-semibold text-sm text-text-muted">{isToday ? 'האימון של היום' : 'האימון הבא'}</span>
        <Badge tone={wk.quality ? 'quality' : 'accent'} variant="solid">{wk.label}</Badge>
      </div>
      <div className="mt-3 font-extrabold text-[24px] text-text tabular-nums" style={{ letterSpacing: '-.4px' }}>
        {next.label || 'אימון'}
        {next.planned_pace && (
          <span className={`text-[20px] ${wk.quality ? 'text-quality' : 'text-accent'}`}> @ {next.planned_pace}</span>
        )}
      </div>
      <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-divider">
        <Metric value={next.planned_distance ? `${next.planned_distance} ק״מ` : '—'} label="מרחק" className="text-center" />
        <Metric value={next.planned_pace || '—'} label="קצב יעד" className="text-center" />
        <Metric value={dayCell || '—'} label="יום" className="text-center" />
      </div>
      <Button className="mt-4" trailingIcon={<CaretLeft size={16} weight="bold" />} onClick={onViewPlan}>
        צפה בתכנית המלאה
      </Button>
    </Card>
  );
}

// (2026-10-27) The training forecast card (ForecastA2) was removed: product decision — no forecast is shown in
// the app at this stage.

// ── Coach insight — framed, the anchor card (§5.1). Compliance verdicts only. ──
// Units of the insight metric tiles (HR 'bpm' = the app's existing convention: ActivityCard,
// InsightLastWorkout). Exported for the render test.
export const INSIGHT_UNITS = { km: 'ק״מ', pace: 'דק׳/ק״מ', hr: 'bpm' };

// The Home deep-analysis button from the backend's per-workout state (trend.deep_analysis_state, 2026-10-23).
// Never offers a generation the backend would refuse; 'analyzed' only opens the saved analysis in the chat.
export function deepAnalysisButton(trend, deepAnalyzing, onDeepAnalyze, onViewDeepAnalysis) {
  const state = trend?.chat_analyzed ? 'analyzed' : (trend?.deep_analysis_state || 'none');
  if (deepAnalyzing || state === 'analyzing') {
    return { state: 'analyzing', label: 'המאמן מנתח לעומק…', disabled: true };
  }
  if (state === 'analyzed') {
    return { state, label: 'צפה בניתוח', disabled: !onViewDeepAnalysis, onClick: onViewDeepAnalysis };
  }
  if (state === 'exhausted') {
    return { state, label: 'ניתוח העומק לא הושלם', disabled: true };
  }
  if (state === 'failed') {
    return { state, label: 'נסה שוב ניתוח עומק', disabled: false, onClick: onDeepAnalyze };
  }
  return { state: 'none', label: 'ניתוח עומק', disabled: false, onClick: onDeepAnalyze };
}

const VERDICT_TONE = {
  on_plan:   { tone: 'positive', label: 'בהתאם לתכנית' },
  deviation: { tone: 'quality',  label: 'סטייה מהתכנון' },
  easy_done: { tone: 'accent',   label: 'קל בכוונה' },
};

export function InsightA2({
  trend, pendingContext, contextSaving,
  onContextSave, onContextSkip, onDeepAnalyze, deepAnalyzing, onViewDeepAnalysis,
}) {
  const [sent, setSent] = useState(false);
  const [showMore, setShowMore] = useState(false);
  if (!trend) return null;
  const tw = trend.today_workout || {};
  const v = VERDICT_TONE[trend.verdict] || null; // 'completed' stays a plain, uncolored label
  const prov = [
    tw.date ? String(tw.date).slice(5, 10).replace('-', '.') : null,
    tw.label,
  ].filter(Boolean).join(' · ') || 'תובנת המאמן';
  const awaitingFeedback = !!pendingContext && !sent;
  const submit = (flag) => {
    if (contextSaving) return;
    setSent(true);
    onContextSave && onContextSave(flag);
  };
  // Provenance-labelled metrics: for a workout with a detected quality segment, pace/HR are
  // SEGMENT values while distance is the whole activity — every number says what it refers to.
  const hasWork = !!tw.quality_distance;
  const hrLabel = tw.hr_kind === 'segment' ? 'דופק עבודה' : tw.hr_kind === 'activity' ? 'דופק ממוצע' : 'דופק';
  // Display contract (BUG-INSIGHT-UNITS): a physical measurement is never a bare number — the API
  // sends plain values (km float, 'm:ss' pace, bpm int); the unit is rendered here, per tile.
  const metrics = (hasWork ? [
    tw.pace && { value: tw.pace, unit: INSIGHT_UNITS.pace, label: 'קצב עבודה' },
    tw.hr && { value: tw.hr, unit: INSIGHT_UNITS.hr, label: hrLabel },
    tw.quality_distance && { value: `${tw.quality_distance}`, unit: INSIGHT_UNITS.km, label: 'קטעי עבודה' },
    tw.distance && { value: `${tw.distance}`, unit: INSIGHT_UNITS.km, label: 'סה״כ' },
  ] : [
    tw.pace && { value: tw.pace, unit: INSIGHT_UNITS.pace, label: 'קצב' },
    tw.hr && { value: tw.hr, unit: INSIGHT_UNITS.hr, label: hrLabel },
    tw.distance && { value: `${tw.distance}`, unit: INSIGHT_UNITS.km, label: 'מרחק' },
  ]).filter(Boolean);

  return (
    <Card>
      <div className="flex items-center gap-2.5">
        <Sparkle size={22} weight="bold" className="text-accent shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-bold text-[16px] text-text">תובנת המאמן</div>
          <div className="font-medium text-[13px] text-text-muted truncate">{prov}</div>
        </div>
        {v && <Badge tone={v.tone} className="shrink-0">{v.label}</Badge>}
      </div>
      <p className="font-medium text-[15px] leading-relaxed text-text-body mt-3.5 mb-0">{trend.insight_text}</p>
      {metrics.length > 0 && (
        <div className={`grid gap-2 mt-3.5 pt-3.5 border-t border-divider`} style={{ gridTemplateColumns: `repeat(${metrics.length}, minmax(0,1fr))` }}>
          {metrics.map((m, i) => <Metric key={i} value={m.value} unit={m.unit} label={m.label} className="text-center" />)}
        </div>
      )}

      {/* Feedback loop — context-flag API ('great' / 'hard_day' + extended options) */}
      <div className="mt-4 pt-3.5 border-t border-divider">
        {sent ? (
          <div className="flex items-center gap-2 rounded-control px-3.5 py-2.5 bg-positive-soft text-positive font-semibold text-sm">
            <Check size={15} weight="bold" />
            האימון נשמר · התכנית עודכנה בהתאם
          </div>
        ) : awaitingFeedback ? (
          <>
            <div className="font-semibold text-xs text-text-muted mb-2">איך הרגשת באימון?</div>
            <div className="flex gap-2">
              <Button variant="tinted" tone="positive" icon={<ThumbsUp size={15} weight="bold" />} disabled={contextSaving} onClick={() => submit('great')}>הרגיש טוב</Button>
              <Button variant="tinted" tone="quality" icon={<ThumbsDown size={15} weight="bold" />} disabled={contextSaving} onClick={() => submit('hard_day')}>היה קשה</Button>
            </div>
            {showMore ? (
              <div className="flex gap-2 mt-2">
                <Button variant="tinted" tone="accent" icon={<Thermometer size={15} weight="bold" />} disabled={contextSaving} onClick={() => submit('sick')}>הייתי חולה</Button>
                <Button variant="tinted" tone="accent" icon={<Sun size={15} weight="bold" />} disabled={contextSaving} onClick={() => submit('hot')}>היה חם</Button>
              </div>
            ) : (
              <button type="button" onClick={() => setShowMore(true)} className="bg-transparent border-none text-text-muted font-medium text-xs mt-2 p-0 cursor-pointer">
                אפשרויות נוספות
              </button>
            )}
            <button
              type="button"
              onClick={() => { setSent(false); onContextSkip && onContextSkip(); }}
              className="block bg-transparent border-none text-text-muted font-medium text-xs mt-1.5 p-0 cursor-pointer"
            >דלג</button>
          </>
        ) : null}

        {/* Deep analysis — core product action, kept as the card's secondary CTA. ONE per workout (2026-10-23,
            cost control): the backend state decides — analysed (incl. with unsupported claims removed) -> view the
            saved analysis, never a new generation; one retry after a failed attempt; exhausted -> no retry. */}
        {onDeepAnalyze && tw.date && (() => {
          const btn = deepAnalysisButton(trend, deepAnalyzing, onDeepAnalyze, onViewDeepAnalysis);
          return (
            <Button
              variant="tinted" tone="accent"
              className={awaitingFeedback || sent ? 'mt-2.5' : ''}
              data-testid="deep-analysis-button"
              data-state={btn.state}
              disabled={btn.disabled}
              onClick={() => { if (!btn.disabled && btn.onClick) btn.onClick(); }}
            >
              {btn.label}
            </Button>
          );
        })()}
      </div>
    </Card>
  );
}

// ── Functional banner — shared shell for state prompts (notifications, injury, incomplete
//    profile, Strava connect, coach invite). One quiet pattern: icon tile in a semantic token
//    color, Heebo copy, optional chip + actions.
//    `accent` is one of the 3 design tokens ('quality' | 'accent' | 'positive') — never a raw hex.
export function A2Banner({ Icon, accent = 'quality', title, body, chip, actions = [], onDismiss }) {
  return (
    <Card accentSide={accent} className="flex items-start gap-3">
      <div
        className="w-[38px] h-[38px] rounded-control flex items-center justify-center shrink-0"
        style={{ background: `var(--${accent}-soft)`, color: `var(--${accent})` }}
      >
        <Icon size={18} weight="bold" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold text-sm text-text">{title}</span>
          {chip && <span className="font-medium text-[11px] text-text-muted bg-surface-2 border border-border rounded-badge px-1.5 py-0.5">{chip}</span>}
        </div>
        {body && <div className="font-medium text-[12.5px] text-text-muted mt-1 leading-relaxed">{body}</div>}
        {actions.length > 0 && (
          <div className="flex gap-2 mt-2 flex-wrap">
            {actions.map((a, i) => (
              <Button
                key={i}
                fullWidth={false}
                variant={a.kind === 'ghost' ? 'secondary' : 'tinted'}
                tone={accent}
                className="px-3.5 h-auto py-1.5 text-[12.5px]"
                onClick={a.onClick}
              >{a.label}</Button>
            ))}
          </div>
        )}
      </div>
      {onDismiss && (
        <button type="button" onClick={onDismiss} aria-label="סגור" className="bg-transparent border-none text-text-muted text-base p-0.5 leading-none cursor-pointer">✕</button>
      )}
    </Card>
  );
}

// ── Chat card (§5.1 item 6) — the most important entry point, a framed card, never a list row.
//    C1 (no active thread): up to two data-derived suggested questions + "שאל את המאמן".
//    C2 (active thread within 72h): quoted last coach message + "המשך את השיחה".
//    Behavioral spec (state selection, trigger rules, tap-consumes-question lifecycle) per
//    handoff_chat_card/README.md — that doc's visual spec (hex colors, serif quote, gradient
//    tile) is superseded by the current design tokens; only its C1/C2/C3 behavior applies. ──
export function ChatCardA2({ data, loading, remaining, locked, onOpen, onAsk }) {
  const quota = data?.quota?.remaining ?? remaining;
  const exhausted = quota === 0;
  const isC2 = !locked && data?.state === 'c2' && data?.active_thread;
  const questions = (!locked && data?.questions) || [];
  const quotaLine = locked ? 'זמין במנוי — ₪19.90/חודש'
    : exhausted ? 'המכסה היומית הסתיימה · מתחדשת בחצות'
    : quota != null ? `נותרו ${quota} הודעות היום` : 'שאל כל שאלה על האימונים שלך';

  return (
    <Card style={{ borderColor: 'var(--g-line)' }}>
      <button type="button" onClick={onOpen} className="w-full flex items-center gap-3 text-start">
        <span className="w-10 h-10 rounded-control bg-accent text-on-accent shrink-0 flex items-center justify-center">
          <ChatCircle size={21} weight="bold" />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block font-bold text-[17px] text-text">צ&#39;אט עם המאמן</span>
          {isC2 && data.active_thread.when && (
            <span className="block font-medium text-[13px] text-text-muted mt-0.5">הודעה אחרונה {data.active_thread.when}</span>
          )}
        </span>
      </button>

      {isC2 ? (
        <>
          <div className="mt-3.5 ps-[13px] border-s-2 border-accent">
            <p
              className="m-0 font-medium text-[14px] leading-relaxed text-text-body"
              style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
            >{`״${data.active_thread.last_coach_message}״`}</p>
          </div>
          <Button variant="tinted" tone="accent" className="mt-3.5" onClick={onOpen}>המשך את השיחה</Button>
        </>
      ) : (
        <>
          {(loading || questions.length > 0) && (
            <div className="mt-3.5 pt-3.5 border-t border-divider flex flex-col gap-2">
              {loading
                ? [0, 1].map((i) => <Skeleton key={i} rounded="control" className="h-11" />)
                : questions.map((q, i) => (
                  <button
                    key={q.key}
                    type="button"
                    disabled={exhausted}
                    onClick={() => onAsk?.(q)}
                    className={`flex items-center gap-2 w-full text-start rounded-control px-3.5 py-3 text-[13px] font-semibold disabled:opacity-50 disabled:pointer-events-none ${
                      i === 0 ? 'bg-accent-soft text-text' : 'bg-surface-2 border border-border text-text-body'
                    }`}
                    style={i === 0 ? { border: '1px solid var(--g-accent)' } : undefined}
                  >
                    {(q.key === 'race_prep' || q.key === 'race_taper')
                      ? <Flag size={14} weight="bold" className={i === 0 ? 'text-accent shrink-0' : 'text-text-muted shrink-0'} />
                      : <Question size={14} weight="bold" className={i === 0 ? 'text-accent shrink-0' : 'text-text-muted shrink-0'} />}
                    <span className="flex-1 min-w-0">{q.text}</span>
                  </button>
                ))}
            </div>
          )}
          <Button className="mt-3.5" onClick={onOpen}>שאל את המאמן</Button>
        </>
      )}

      <div className="text-center font-medium text-xs text-text-muted mt-3">{quotaLine}</div>
    </Card>
  );
}

// ── Quick-access list ("עוד באפליקציה"). premium (PREMIUM_TODAY_ENABLED, 2026-10-23): Plan and Activities are in
//    the fixed bottom bar, so the list is "גישה מהירה" with the races row only. Same routes either way. ──
export function QuickAccessList({ premium = false, planSub, actSub, raceSub, onPlan, onActivities, onRaces }) {
  return (
    <Section title={premium ? 'גישה מהירה' : 'עוד באפליקציה'}>
    <ListGroup>
      {!premium && (
        <ListRow
          icon={<CalendarBlank weight="bold" />}
          title="תכנית אימונים"
          sub={planSub}
          onClick={onPlan}
        />
      )}
      {!premium && (
        <ListRow
          icon={<ChartLineUp weight="bold" />}
          title="האימונים שלי"
          sub={actSub}
          onClick={onActivities}
        />
      )}
      <ListRow
        icon={<Trophy weight="bold" />}
        title="התחרויות שלי"
        sub={raceSub}
        onClick={onRaces}
      />
    </ListGroup>
    </Section>
  );
}

// ── Privileged section (§5.1 item 8) — second list group under "הרשאות מורחבות", role-gated,
//    same row component as the quick-access list (§4.3), but quieter: a muted label (not a full
//    Section title) and a surface-2 fill so it reads as secondary to the screen's main content. ──
export function PrivilegedSection({ showCoach, showAdmin, onCoach, onAdmin }) {
  if (!showCoach && !showAdmin) return null;
  return (
    <div className="flex flex-col gap-2.5">
      <span className="font-semibold text-[13px] text-text-muted">הרשאות מורחבות</span>
      <ListGroup>
        {showCoach && (
          <ListRow icon={<UserPlus weight="bold" />} title="אזור מאמנים" sub="ניהול קבוצת רצים · דשבורד מאמן" onClick={onCoach} />
        )}
        {showAdmin && (
          <ListRow
            icon={<Gear weight="bold" />}
            title={<span className="inline-flex items-center gap-2">ניהול מערכת
              <span className="font-bold text-[11px] text-accent border border-accent rounded-[5px] px-1.5 py-0.5">Admin</span>
            </span>}
            sub="ניהול משתמשים והרשאות"
            onClick={onAdmin}
          />
        )}
      </ListGroup>
    </div>
  );
}

